/**
 * AS EMPRESAS DO GRUPO.
 *
 * O RH atende mais de uma empresa na mesma base: a SERCAMP e a Move Me. Elas
 * têm formulários de avaliação de desempenho DIFERENTES, e o mesmo cargo
 * ("CONSULTOR COMERCIAL") existe nas duas — por isso a empresa precisa ser um
 * dado do colaborador, e não um palpite pelo cargo ou pelo setor.
 *
 * Fica em src/ (e não no servidor) porque as duas pontas usam: a tela filtra a
 * lista por empresa e o servidor decide o formulário de cada colaborador.
 */

export type EmpresaKey = "sercamp" | "moveme";

export interface Empresa {
  key: EmpresaKey;
  /** Como aparece na tela e no cabeçalho do formulário impresso. */
  nome: string;
  /** O que o RH costuma digitar no campo "Contrato" de quem é dessa empresa. */
  contratos: string[];
}

export const EMPRESAS: Empresa[] = [
  { key: "sercamp", nome: "SERCAMP", contratos: ["SERCAMP", "NX", "PJ Sercamp", "Pró Labore"] },
  { key: "moveme", nome: "Move Me", contratos: ["MRN", "PJ Morenna"] },
];

export const EMPRESA_PADRAO: EmpresaKey = "sercamp";

export function empresaPorKey(key: unknown): Empresa {
  return EMPRESAS.find((e) => e.key === String(key ?? "")) ?? EMPRESAS[0]!;
}

export function nomeDaEmpresa(key: unknown): string {
  return empresaPorKey(key).nome;
}

/** Compara ignorando acento, caixa e pontuação. */
function norm(s: unknown): string {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^a-z0-9 ]/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

/** Texto que identifica a Move Me no campo "Contrato" (MRN = Morenna). */
const MARCAS_MOVEME = /\b(mrn|morenna|move ?me)\b/;

/**
 * De qual empresa é o colaborador.
 *
 * O campo `empresa` do cadastro manda. Quando ele está vazio — o caso de todo
 * mundo que foi cadastrado antes deste campo existir — a resposta sai do
 * CONTRATO, que é onde o RH já vinha marcando a diferença ("MRN"/"PJ Morenna"
 * para a Move Me). Assim os cadastros antigos já entram certos, sem ninguém
 * precisar reeditar 85 fichas.
 *
 * Na dúvida é SERCAMP: é a maioria da base, e o RH corrige na ficha.
 */
export function empresaDoColaborador(
  colaborador: { empresa?: unknown; contrato?: unknown } | null | undefined,
): EmpresaKey {
  const explicita = norm(colaborador?.empresa);
  if (explicita) {
    const achada = EMPRESAS.find((e) => e.key === explicita || norm(e.nome) === explicita);
    if (achada) return achada.key;
    if (MARCAS_MOVEME.test(explicita)) return "moveme";
  }

  const contrato = norm(colaborador?.contrato);
  if (contrato && MARCAS_MOVEME.test(contrato)) return "moveme";

  return EMPRESA_PADRAO;
}

/** Filtra uma lista de colaboradores pela empresa. */
export function daEmpresa<T extends { empresa?: unknown; contrato?: unknown }>(
  colaboradores: T[],
  empresa: EmpresaKey,
): T[] {
  return colaboradores.filter((c) => empresaDoColaborador(c) === empresa);
}
